import {
  useRef, useEffect, ReactNode, ReactComponentElement,
} from 'react';

import {
  ButtonContainer,
  CloseButton,
  ModalContainer,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Button,
  CloseButtonName,
} from './styles';
import { CloseModalX } from '~/icons';
import { t } from 'i18next';
import LogoCelciusColor from '../../assets/img/logos/LogoCelciusColor.svg';

export type Variant = 'large';

interface IModalTemplate {
  title?: string;
  button1?: string | { label: string, options: ReactComponentElement<'button'>['props'] & { primary?: boolean }}
  button2?: string;
  iconTitle?: ReactNode;
  isOpen: boolean;
  text?: string;
  hasCloseButton?: boolean;
  imageText?: ReactNode;
  additionalText?: ReactNode;
  fontSizeText?: number;
  removeXCloseButton?: boolean;
  centralizedText?: boolean;
  button1Action?: () => void;
  button2Action?: () => void;
  onClose: () => void;
  padding?: number;
  customButton?: ReactNode;
  dataTestId?: string;
  variant?: Variant;
  fullScreenMobile?: boolean;
}

export const ModalTemplate: React.FC<IModalTemplate> = ({
  title, button1, button2, isOpen, onClose, iconTitle, text, button1Action, button2Action, imageText, centralizedText = false, hasCloseButton, fontSizeText = 16, removeXCloseButton = false, additionalText, padding = 28, customButton, dataTestId, variant, fullScreenMobile,
}): JSX.Element => {
  const refModal = useRef<HTMLDivElement>(null);

  function isSnackbarComponent(target: HTMLElement): boolean {
    let element: HTMLElement | null = target;

    while (element !== null) {
      if (element.classList.contains('Toastify')) {
        return true;
      }
      element = element.parentElement;
    }

    return false;
  }

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as HTMLElement;

      if (refModal.current && !refModal.current.contains(target) && !isSnackbarComponent(target)) {
        onClose();
      }
    }

    function handleKeyClose(event: KeyboardEvent) {
      if (isOpen && event.key === 'Escape') {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyClose);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyClose);
    };
  }, [isOpen, onClose]);

  return (
    <ModalOverlay isOpen={isOpen}>
      <ModalContainer
        ref={refModal}
        padding={padding}
        variant={variant}
        fullScreenMobile={fullScreenMobile}
        data-testid={dataTestId}
      >
        {
          (!title && !iconTitle) ? null : (
            <ModalHeader fullScreenMobile={fullScreenMobile}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {iconTitle}
                {title}
              </div>
              {removeXCloseButton ? null : (
                <CloseButton onClick={onClose}>
                  <CloseModalX />
                </CloseButton>
              )}
              {fullScreenMobile && (<img src={LogoCelciusColor} alt="Celsius Logo" />)}
            </ModalHeader>
          )
        }
        <ModalContent style={{ textAlign: centralizedText ? 'center' : 'left', fontSize: fontSizeText || 16 }}>
          {text}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {imageText}
          </div>
          {additionalText}
        </ModalContent>
        <ButtonContainer
          fullScreenMobile={fullScreenMobile}
          hide={customButton === undefined && button1 === undefined && button2 === undefined && !hasCloseButton}
        >
          {customButton}
          {button1 && (
          <Button
            primary
            onClick={button1Action}
            {...(typeof button1 !== 'string' && { ...button1.options })}
          >
            {typeof button1 === 'string' ? button1 : button1.label }
          </Button>
          )}
          {button2 && <Button onClick={button2Action}>{button2}</Button>}
          {hasCloseButton && <CloseButtonName onClick={onClose}>{t('fechar')}</CloseButtonName>}
        </ButtonContainer>
      </ModalContainer>
    </ModalOverlay>
  );
};
